import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";

function plateTex(color, label) {
  const c = document.createElement("canvas");
  c.width = 128;
  c.height = 96;
  const g = c.getContext("2d");
  g.fillStyle = "#f4f4f4";
  g.fillRect(0, 0, 128, 96);
  g.fillStyle = color;
  g.fillRect(0, 78, 128, 18);
  g.fillStyle = "#111";
  g.font = "bold 64px monospace";
  g.textAlign = "center";
  g.fillText(label, 64, 66);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

function tyreGeo(r, w) {
  const pts = [];
  const e = w * 0.22;
  pts.push(new THREE.Vector2(r * 0.62, -w / 2));
  for (let i = 0; i <= 6; i++) {
    const a = (i / 6) * (Math.PI / 2);
    pts.push(new THREE.Vector2(r - e + Math.sin(a) * e, -w / 2 + e - Math.cos(a) * e));
  }
  for (let i = 0; i <= 6; i++) {
    const a = (i / 6) * (Math.PI / 2);
    pts.push(new THREE.Vector2(r - e + Math.cos(a) * e, w / 2 - e + Math.sin(a) * e));
  }
  pts.push(new THREE.Vector2(r * 0.62, w / 2));
  return new THREE.LatheGeometry(pts, 28);
}

export function kart(color, label, ghost = false) {
  const g = new THREE.Group();
  const mat = (o) => new THREE.MeshStandardMaterial({ ...o, ...(ghost ? { transparent: true, opacity: 0.45 } : {}) });
  const hex = `#${new THREE.Color(color).getHexString()}`;
  const M = {
    paint: mat({ color, roughness: 0.25, metalness: 0.15 }),
    suit: mat({ color: new THREE.Color(color).multiplyScalar(0.55), roughness: 0.8 }),
    dark: mat({ color: 0x1b1d23, roughness: 0.55, metalness: 0.3 }),
    tube: mat({ color: 0x9aa0aa, roughness: 0.25, metalness: 0.9 }),
    alu: mat({ color: 0xb8bcc4, roughness: 0.35, metalness: 0.85 }),
    tyre: mat({ color: 0x111113, roughness: 0.95 }),
    rim: mat({ color: 0xd9a400, roughness: 0.3, metalness: 0.8 }),
    visor: mat({ color: 0x0a0c12, roughness: 0.05, metalness: 0.9 }),
    white: mat({ color: 0xf2f2f2, roughness: 0.4 }),
    plate: mat({ map: plateTex(hex, label), roughness: 0.5 }),
  };
  const add = (geo, m, [x, y, z], rot = [0, 0, 0], parent = g) => {
    const o = new THREE.Mesh(geo, m);
    o.position.set(x, y, z);
    o.rotation.set(...rot);
    o.castShadow = !ghost;
    parent.add(o);
    return o;
  };
  const rb = (w, h, d, r = 0.05) => new RoundedBoxGeometry(w, h, d, 3, r);
  const cyl = (r, l, s = 12) => new THREE.CylinderGeometry(r, r, l, s);

  add(rb(0.62, 0.025, 1.45, 0.01), M.dark, [0, 0.07, 0]);
  [-0.3, 0.3].forEach((x) => add(cyl(0.016, 1.5), M.tube, [x, 0.08, 0], [Math.PI / 2, 0, 0]));
  add(cyl(0.016, 1.1), M.tube, [0, 0.08, -0.6], [0, 0, Math.PI / 2]);
  add(cyl(0.02, 1.2), M.tube, [0, 0.14, 0.62], [0, 0, Math.PI / 2]);

  [-1, 1].forEach((s) => {
    add(rb(0.24, 0.17, 0.74, 0.07), M.paint, [s * 0.56, 0.2, 0.06]);
    add(rb(0.245, 0.04, 0.5, 0.02), M.white, [s * 0.56, 0.26, 0.06]);
  });
  add(rb(1.0, 0.16, 0.36, 0.07), M.paint, [0, 0.19, -0.88], [0.1, 0, 0]);
  add(rb(0.5, 0.06, 0.3, 0.03), M.dark, [0, 0.29, -0.86], [0.1, 0, 0]);
  add(rb(0.36, 0.3, 0.035, 0.03), M.paint, [0, 0.36, -0.6], [-0.38, 0, 0]);
  add(new THREE.PlaneGeometry(0.28, 0.21), M.plate, [0, 0.37, -0.625], [-0.38 + Math.PI, 0, Math.PI]);
  add(rb(1.3, 0.13, 0.16, 0.06), M.paint, [0, 0.17, 0.98]);

  const driver = new THREE.Group();
  g.add(driver);
  add(rb(0.42, 0.44, 0.36, 0.1), M.dark, [0, 0.36, 0.27], [-0.2, 0, 0], driver);
  add(rb(0.24, 0.24, 0.28, 0.04), M.alu, [0.46, 0.3, 0.36]);
  add(cyl(0.09, 0.14, 16), M.alu, [0.46, 0.48, 0.36]);
  add(cyl(0.035, 0.5), M.tube, [0.5, 0.3, 0.68], [Math.PI / 2, 0, 0]);
  add(cyl(0.05, 0.22, 14), M.dark, [0.52, 0.32, 0.86], [Math.PI / 2, 0, 0]);

  add(cyl(0.014, 0.5), M.tube, [0, 0.36, -0.38], [-1.05, 0, 0]);
  const sw = add(new THREE.TorusGeometry(0.13, 0.018, 8, 28), M.dark, [0, 0.57, -0.16], [-1.05, 0, 0]);
  add(rb(0.1, 0.06, 0.03, 0.01), M.alu, [0, 0, 0], [Math.PI / 2, 0, 0], sw);

  add(rb(0.4, 0.46, 0.26, 0.1), M.suit, [0, 0.66, 0.2], [-0.15, 0, 0], driver);
  add(rb(0.42, 0.04, 0.27, 0.02), M.paint, [0, 0.82, 0.19], [-0.15, 0, 0], driver);
  [-1, 1].forEach((s) => {
    const arm = add(cyl(0.045, 0.42), M.suit, [s * 0.15, 0.68, -0.03], [-1.25, 0, s * -0.25], driver);
    arm.castShadow = !ghost;
    add(new THREE.SphereGeometry(0.04, 10, 8), M.dark, [s * 0.11, 0.6, -0.2], [0, 0, 0], driver);
  });
  add(cyl(0.05, 0.1), M.suit, [0, 0.92, 0.18], [0, 0, 0], driver);
  add(new THREE.SphereGeometry(0.15, 24, 18), M.paint, [0, 1.04, 0.17], [0, 0, 0], driver);
  add(new THREE.TorusGeometry(0.151, 0.012, 6, 32), M.white, [0, 1.04, 0.17], [0, Math.PI / 2, 0], driver);
  add(new THREE.SphereGeometry(0.152, 20, 10, -0.9, 1.8, 1.25, 0.55), M.visor, [0, 1.04, 0.17], [0, Math.PI, 0], driver);

  const wheel = (r, w, x, z) => {
    const wg = new THREE.Group();
    add(tyreGeo(r, w), M.tyre, [0, 0, 0], [0, 0, 0], wg);
    add(cyl(r * 0.63, w * 0.92, 18), M.rim, [0, 0, 0], [0, 0, 0], wg);
    add(cyl(r * 0.2, w + 0.02, 10), M.alu, [0, 0, 0], [0, 0, 0], wg);
    wg.rotation.z = Math.PI / 2;
    wg.position.set(x, r, z);
    g.add(wg);
  };
  wheel(0.14, 0.21, -0.62, 0.6);
  wheel(0.14, 0.21, 0.62, 0.6);
  wheel(0.125, 0.13, -0.56, -0.56);
  wheel(0.125, 0.13, 0.56, -0.56);
  g.userData.driver = driver;
  return g;
}
